import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlarmClock, CheckCheck, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AppShell } from "@/components/AppShell";
import { PunchCard } from "@/components/PunchCard";
import { api, todayKL } from "@/lib/api";
import { useLang } from "@/lib/i18n";

/* Supervisor daily review queue — default yesterday + today, pending first (spec §8). */
export default function ReviewQueuePage() {
  const { t } = useLang();
  const queryClient = useQueryClient();
  const [dateFrom, setDateFrom] = useState(todayKL(-1));
  const [dateTo, setDateTo] = useState(todayKL(0));
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState({});

  const { data, isLoading } = useQuery({
    queryKey: ["queue", dateFrom, dateTo, statusFilter],
    queryFn: async () => (await api.get("/punches/queue", {
      params: {
        dateFrom, dateTo,
        reviewStatus: statusFilter === "all" ? "" : statusFilter,
      },
    })).data,
    refetchOnMount: "always",
  });

  const reviewMutation = useMutation({
    mutationFn: async ({ id, body }) => (await api.post(`/punches/${id}/review`, body)).data,
    onSuccess: () => {
      toast.success(t("done"));
      queryClient.invalidateQueries({ queryKey: ["queue"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  // Deletion is ADMIN-ONLY — supervisors never get the delete affordance
  const deleteMutation = useMutation({
    mutationFn: async (id) => (await api.delete(`/punches/${id}`)).data,
    onSuccess: () => {
      toast.success(t("punchDeleted"));
      queryClient.invalidateQueries({ queryKey: ["queue"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const bulkMutation = useMutation({
    mutationFn: async (ids) => (await api.post("/punches/bulk-approve", { punchIds: ids })).data,
    onSuccess: (res) => {
      toast.success(`${t("approve")}: ${res.approved}`);
      setSelected({});
      queryClient.invalidateQueries({ queryKey: ["queue"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const items = data?.items || [];

  const { data: digest } = useQuery({
    queryKey: ["late-digest"],
    queryFn: async () => (await api.get("/punches/late-digest")).data,
    refetchOnMount: "always",
    refetchInterval: 5 * 60 * 1000,
  });

  return (
    <AppShell>
      <div className="space-y-4 pb-24">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-xl font-semibold tracking-tight">{t("reviewQueue")}</h1>
          <div className="flex items-center gap-2">
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              data-testid="queue-date-from" className="h-9 w-auto" />
            <span className="text-muted-foreground">–</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              data-testid="queue-date-to" className="h-9 w-auto" />
          </div>
        </div>

        {/* Late alerts digest — in-app morning summary (no emails/notifications) */}
        {digest && (
          <Card data-testid="late-digest-card" className="shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                <AlarmClock className="h-4 w-4 text-amber-600" />
                {t("lateDigest")}
                <span className="ml-auto flex flex-wrap gap-2 text-xs font-normal text-muted-foreground">
                  <span data-testid="digest-clocked-in">{digest.clockedInCount} {t("clockedInCount")}</span>
                  <span>· {digest.notYetDueCount} {t("notYetDueCount")}</span>
                  <span>· {digest.noHistoryCount} {t("noHistoryCount")}</span>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {digest.late.length === 0 ? (
                <p data-testid="digest-all-clear" className="flex items-center gap-2 text-sm text-emerald-700">
                  <CircleCheck className="h-4 w-4" /> {t("allClockedIn")}
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {digest.late.map((r) => (
                    <li key={r.userId} data-testid="digest-late-row"
                        className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
                      <span className="font-medium">{r.userName}</span>
                      <span className="text-xs text-muted-foreground">{r.employeeId}</span>
                      <span className="ml-auto text-xs text-muted-foreground">
                        {t("usualTime")}: <span className="font-mono tabular-nums">{r.usualTime}</span>
                      </span>
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-900">
                        +{r.minutesLate} {t("minLate")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList className="flex-wrap">
            <TabsTrigger value="all" data-testid="queue-tab-all">{t("allStatuses")}</TabsTrigger>
            <TabsTrigger value="pending" data-testid="queue-tab-pending">{t("pending")}</TabsTrigger>
            <TabsTrigger value="approved" data-testid="queue-tab-approved">{t("approved")}</TabsTrigger>
            <TabsTrigger value="flagged" data-testid="queue-tab-flagged">{t("flagged")}</TabsTrigger>
            <TabsTrigger value="overridden" data-testid="queue-tab-overridden">{t("overridden")}</TabsTrigger>
          </TabsList>
        </Tabs>

        {isLoading && [0, 1, 2].map((i) => <Skeleton key={i} className="h-36 w-full rounded-xl" />)}
        {!isLoading && items.length === 0 && (
          <p data-testid="queue-empty" className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
            {t("noResults")}
          </p>
        )}
        <div className="space-y-3">
          {items.map((p) => (
            <PunchCard
              key={p.id}
              punch={p}
              busy={reviewMutation.isPending || deleteMutation.isPending}
              selectable
              selected={Boolean(selected[p.id])}
              onSelectChange={(v) => setSelected((s) => ({ ...s, [p.id]: v }))}
              onReview={(id, body) => reviewMutation.mutateAsync({ id, body })}
              onDelete={(id) => deleteMutation.mutate(id)}
            />
          ))}
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card/95 p-3 backdrop-blur-sm rk-safe-bottom">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4">
            <span className="text-sm font-medium">{selectedIds.length} {t("punchesSelected")}</span>
            <Button
              data-testid="bulk-approve-button"
              disabled={bulkMutation.isPending}
              onClick={() => bulkMutation.mutate(selectedIds)}
              className="bg-emerald-700 hover:bg-emerald-700/90"
            >
              <CheckCheck className="mr-2 h-4 w-4" /> {t("bulkApprove")}
            </Button>
          </div>
        </div>
      )}
    </AppShell>
  );
}
